import type { ActionTypeId } from '@seoplum/core'
import { ACTION_TYPES, OUTREACH_BOUNCE_PAUSE_RATE } from '@seoplum/core'
import { createFileRoute } from '@tanstack/react-router'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { Fragment, useState } from 'react'
import { toast } from 'sonner'
import { Panel } from '@/components/panel'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  type DeliverabilityRow,
  type FeedName,
  type FeedRow,
  type OutreachEfficacyData,
  type OutreachOpsData,
  type OutreachSiteRow,
  type OutreachSourceEfficacyRow,
  type OutreachSweep,
  type OutreachVersionEfficacyRow,
  type SuppressionRow,
  type TriggerSpec,
  useClearOutreachPause,
  useFeeds,
  useOutreachEfficacy,
  useOutreachOps,
  useOutreachSweep,
  useSetBacklinkBudget,
  useTriggerJob,
} from '@/lib/admin'
import { formatExact, formatPercent } from '@/lib/format'
import { formatMoney } from '@/lib/money'
import { cn } from '@/lib/utils'
import { Age, age, Tag, Th } from './-parts'

export const Route = createFileRoute('/_app/admin/feeds')({
  component: FeedsPage,
})

/**
 * How stale every site is, per feed, across every tenant.
 *
 * The customer-facing reports say none of this by design — not when we last
 * looked, not how often we will look again, not what looking cost. Staff need
 * all three to answer "why does this site look wrong", so it lives here, on the
 * one page that is allowed to be about our plumbing rather than about search.
 *
 * Ordered by workspace and host rather than by staleness: the question is
 * usually asked about a site somebody has just been emailed about, and a table
 * that reorders itself as jobs finish is one you cannot find anything in.
 */
function FeedsPage() {
  const { data, isPending } = useFeeds()
  const outreach = useOutreachOps()
  const efficacy = useOutreachEfficacy()
  const [open, setOpen] = useState<string | null>(null)

  if (isPending || !data) return <p className="text-[13px] text-muted-foreground">Loading…</p>

  const now = Date.parse(data.now)
  const states = new Map((outreach.data?.sites ?? []).map((row) => [row.siteId, row]))

  return (
    <div className="space-y-4">
      <Panel title="Sites" meta={formatExact(data.sites.length)} flush>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="py-2 pr-3 pl-3 text-left">
                  <span className="mono-label text-muted-foreground">Site</span>
                </th>
                <Th>Search</Th>
                <Th>Sitemap</Th>
                <Th>Backlinks</Th>
                <Th>Outreach</Th>
                <Th>Spend</Th>
                <th className="w-8" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {data.sites.map((site) => (
                <SiteRow
                  key={site.siteId}
                  site={site}
                  outreach={states.get(site.siteId) ?? null}
                  now={now}
                  triggers={data.triggers}
                  open={open === site.siteId}
                  onToggle={() => setOpen(open === site.siteId ? null : site.siteId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {outreach.data && (
        <Outreach data={outreach.data} sweeps={data.sweeps.filter((s) => s.feed === 'outreach')} />
      )}

      {efficacy.data && <Efficacy data={efficacy.data} />}
    </div>
  )
}

/**
 * The one place in the product where a row unfolds rather than opening a page.
 *
 * The rule it breaks is a reading rule — a row cannot hold a chart — and there
 * is no chart here. What the drawer holds is controls, and staff work down this
 * table site by site: sending each of nine sites to its own page and back is
 * the interaction the rule was written to prevent, not the one it forbids.
 */
function SiteRow({
  site,
  outreach,
  now,
  triggers,
  open,
  onToggle,
}: {
  site: FeedRow
  /** Null where the site has never had outreach switched on, which is most of
   * them: the feed sends mail in the customer's name and is off until somebody
   * says otherwise. */
  outreach: OutreachSiteRow | null
  now: number
  triggers: TriggerSpec[]
  open: boolean
  onToggle: () => void
}) {
  const trigger = useTriggerJob()
  const budget = useSetBacklinkBudget()
  const [draft, setDraft] = useState<string | null>(null)
  const [types, setTypes] = useState<ActionTypeId[]>([])
  const [dryRun, setDryRun] = useState(false)

  const current = ((site.backlinkBudgetCents ?? 200) / 100).toFixed(2)
  const value = draft ?? current
  const parsed = Number(value)
  const valid = Number.isFinite(parsed) && parsed >= 0 && parsed <= 1000
  const dirty = valid && Math.round(parsed * 100) !== site.backlinkBudgetCents

  const outreachState = outreachNote(outreach, now)

  return (
    <>
      <tr
        className="cursor-pointer border-b border-border last:border-b-0 hover:bg-secondary/40"
        onClick={onToggle}
      >
        <td className="py-2 pr-3 pl-3">
          <span className="block font-mono text-[12.5px]">{site.host}</span>
          <span className="block text-[11px] text-muted-foreground">{site.organization}</span>
        </td>

        {/* Search carries two facts that a single date cannot separate: the
            newest day Google has answered for, and whether we are still asking. */}
        <Cell
          at={site.searchLastRunAt}
          now={now}
          note={
            site.searchError
              ? 'error'
              : site.searchBackfilling
                ? 'backfilling'
                : (site.searchLatestDate ?? undefined)
          }
          tone={site.searchError ? 'bad' : undefined}
        />
        <Cell
          at={site.sitemapLastFetchedAt}
          now={now}
          note={site.sitemapDocuments > 0 ? `${site.sitemapDocuments} docs` : undefined}
        />
        <Cell
          at={site.backlinkLastSyncedAt}
          now={now}
          note={
            site.backlinkCappedAt
              ? 'capped'
              : site.referringDomains > 0
                ? `${formatExact(site.referringDomains)} domains`
                : undefined
          }
          tone={site.backlinkCappedAt ? 'warn' : undefined}
        />

        {/* The tick is the age; the note is the sweep that feeds it. Both,
            because a pipeline advancing every hour over a prospect list nothing
            has refilled in a month is a feed that has quietly run out. */}
        <Cell
          at={outreach?.lastTickAt ?? null}
          now={now}
          note={outreachState.note}
          tone={outreachState.tone}
        />

        <td className="px-3 py-2 text-right align-middle whitespace-nowrap">
          <span className="font-mono text-[12px] tabular">
            {formatMoney(site.backlinkSpentCents / 100)}
          </span>
          <span className="font-mono text-[11px] text-muted-foreground tabular">
            {' / '}
            {site.backlinkBudgetCents === null
              ? 'default'
              : formatMoney(site.backlinkBudgetCents / 100)}
          </span>
        </td>

        <td className="pr-3 text-right align-middle">
          <span className="mono-label text-muted-foreground">{open ? '−' : '+'}</span>
        </td>
      </tr>

      {open && (
        <tr className="border-b border-border bg-secondary/20">
          <td colSpan={7} className="px-3 py-3">
            <div className="space-y-2">
              {/* Grouped by the feed each one drives. Nine buttons in a single
                  wrap is a row nobody reads, and the feed is the only grouping
                  the person pressing one is thinking in. */}
              {groupByFeed(triggers).map(([feed, specs]) => (
                <div key={feed} className="flex flex-wrap items-center gap-2">
                  <span className="mono-label w-[70px] shrink-0 text-muted-foreground">{feed}</span>
                  {specs.map((spec) => (
                    <Fragment key={spec.name}>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={trigger.isPending}
                        onClick={() =>
                          trigger.mutate({
                            siteId: site.siteId,
                            trigger: spec.name,
                            ...(spec.name === 'action-scan' ? { types, dryRun } : {}),
                          })
                        }
                      >
                        {spec.label}
                      </Button>

                      {/* The two narrowings the scan accepts sit beside the
                          button they narrow, because a control that changes what
                          another control does anywhere else is a control nobody
                          connects. */}
                      {spec.name === 'action-scan' && (
                        <>
                          <TypePicker types={types} onChange={setTypes} />
                          <button
                            type="button"
                            aria-pressed={dryRun}
                            onClick={() => setDryRun(!dryRun)}
                            className={cn(
                              'inline-flex h-8 cursor-pointer items-center rounded-md border px-2.5 font-mono text-[12px] transition-colors',
                              dryRun
                                ? 'border-border-strong text-foreground'
                                : 'border-border text-muted-foreground hover:text-foreground',
                            )}
                          >
                            dry run
                          </button>
                        </>
                      )}
                    </Fragment>
                  ))}
                </div>
              ))}

              <form
                className="flex items-center gap-2 pt-1"
                onSubmit={(event) => {
                  event.preventDefault()
                  if (!dirty) return
                  budget.mutate(
                    { siteId: site.siteId, monthlyBudgetCents: Math.round(parsed * 100) },
                    { onSuccess: () => setDraft(null) },
                  )
                }}
              >
                <span className="mono-label w-[70px] shrink-0 text-muted-foreground">Ceiling</span>
                <span className="font-mono text-[13px] text-muted-foreground">$</span>
                <Input
                  value={value}
                  inputMode="decimal"
                  aria-label={`Monthly backlink budget for ${site.host}`}
                  aria-invalid={!valid}
                  onChange={(event) => setDraft(event.target.value)}
                  className="h-8 w-24 font-mono tabular"
                />
                <Button size="sm" type="submit" disabled={!dirty || budget.isPending}>
                  Save
                </Button>
              </form>
            </div>

            {trigger.isSuccess && (
              <p className="pt-2 font-mono text-[11px] text-muted-foreground">
                queued {trigger.data?.job}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

/** The order the feeds run in, which is also the order they were built in. A
 * map keyed by the api's own `feed` rather than a prefix rule here, because the
 * allowlist is the one place that knows what a trigger drives. */
const FEED_ORDER: FeedName[] = ['search', 'sitemap', 'backlinks', 'outreach', 'actions']

function groupByFeed(triggers: TriggerSpec[]): [FeedName, TriggerSpec[]][] {
  return FEED_ORDER.map(
    (feed) => [feed, triggers.filter((spec) => spec.feed === feed)] as [FeedName, TriggerSpec[]],
  ).filter(([, specs]) => specs.length > 0)
}

/**
 * The one word under the tick age, and it is a state rather than a count.
 *
 * Off, paused and rehearsing are three different reasons a site is sending
 * nothing, and they are the three an operator confuses at a glance. A sweep age
 * is only interesting once none of them applies.
 */
function outreachNote(
  row: OutreachSiteRow | null,
  now: number,
): { note?: string; tone?: 'warn' | 'bad' } {
  if (!row) return {}
  if (row.pausedAt) return { note: 'paused', tone: 'bad' }
  if (row.discovery?.status === 'failed') return { note: 'sweep failed', tone: 'bad' }
  if (!row.enabled) return { note: 'off' }
  if (row.dryRun) return { note: 'dry run', tone: 'warn' }
  if (!row.discovery) return { note: 'never swept', tone: 'warn' }
  return { note: `swept ${age(row.discovery.startedAt, now)}` }
}

/**
 * Deliverability, and the reason outreach needs a section of its own here at
 * all.
 *
 * Every other feed reads somebody else's data; this one sends mail under a
 * customer's name from a mailbox they configured, so a bounce rate climbing is
 * a failure that is invisible from the prospect list, the queue and the ledger
 * alike — right up until the sending domain is blacklisted and every campaign
 * on it is worth nothing.
 */
function Outreach({ data, sweeps }: { data: OutreachOpsData; sweeps: TriggerSpec[] }) {
  const sweep = useOutreachSweep()

  const sends = new Map(data.deliverability.map((row) => [row.siteId, row]))
  const sent = data.deliverability.reduce((total, row) => total + row.sent, 0)
  const rehearsed = data.deliverability.reduce((total, row) => total + row.sentDryRun, 0)

  return (
    <div className="space-y-4">
      <Panel
        title="Outreach"
        meta={`${data.days}d · ${formatExact(sent)} sent · ${formatExact(rehearsed)} rehearsed`}
        actions={
          <div className="flex items-center gap-2">
            {sweeps.map((spec) => (
              <Button
                key={spec.name}
                size="sm"
                variant="outline"
                disabled={sweep.isPending}
                onClick={() =>
                  sweep.mutate(spec.name as OutreachSweep, {
                    onSuccess: (result) => toast.success(`${result.job} queued`),
                    onError: (error) =>
                      toast.error(error instanceof Error ? error.message : 'Failed to queue'),
                  })
                }
              >
                {spec.label}
              </Button>
            ))}
          </div>
        }
        flush
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <Th align="left">Site</Th>
                <Th>Prospects</Th>
                <Th>Drafts</Th>
                <Th>Sent</Th>
                <Th>Dry run</Th>
                <Th>Bounced</Th>
                <Th>Replied</Th>
                <Th>Won</Th>
                <Th>Bounce rate</Th>
              </tr>
            </thead>
            <tbody>
              {data.sites.map((row) => (
                <OutreachRow key={row.siteId} site={row} sends={sends.get(row.siteId) ?? null} />
              ))}
              {data.sites.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center">
                    <span className="font-mono text-[12px] text-muted-foreground">
                      no site has outreach on
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Suppressions rows={data.suppressions} />
    </div>
  )
}

function OutreachRow({
  site,
  sends,
}: {
  site: OutreachSiteRow
  /** Null where the site sent, rehearsed and heard nothing in the window. The
   * row still draws: a configured site that has gone quiet is the finding. */
  sends: DeliverabilityRow | null
}) {
  const clear = useClearOutreachPause()

  const rate = sends?.bounceRate ?? null
  const over = rate !== null && rate >= OUTREACH_BOUNCE_PAUSE_RATE

  return (
    <tr className="border-b border-border last:border-b-0">
      <td className="py-2 pr-3 pl-3">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[12.5px]">{site.host}</span>
          {site.pausedAt && <Tag tone="bad">paused</Tag>}
          {!site.enabled && <Tag>off</Tag>}
          {site.dryRun && <Tag tone="warn">dry run</Tag>}
          {!site.smtpConfigured && <Tag>no smtp</Tag>}
        </span>
        <span className="block text-[11px] text-muted-foreground">
          {site.organization}
          {site.senderEmail ? ` · ${site.senderEmail}` : ''}
          {site.dailyCap === null ? '' : ` · ${site.dailyCap}/day`}
        </span>

        {/* The reason, then the control, in that order: clearing a pause is a
            decision somebody makes after reading why it was set, and the site
            paused itself precisely because nobody was watching. */}
        {site.pausedAt && (
          <span className="flex flex-wrap items-center gap-2 pt-1.5">
            <span className="font-mono text-[11px] text-destructive">
              {site.pausedReason ?? 'paused'}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={clear.isPending}
              onClick={() =>
                clear.mutate(site.siteId, {
                  onSuccess: () => toast.success(`${site.host} may send again`),
                  onError: (error) =>
                    toast.error(error instanceof Error ? error.message : 'Failed to clear'),
                })
              }
            >
              Clear pause
            </Button>
          </span>
        )}
      </td>

      <Figure value={site.prospects} note={`${formatExact(site.researched)} read`} />
      <Figure value={site.drafts} />
      <Figure value={sends?.sent ?? 0} />
      {/* Its own column, never folded into the one beside it. A rehearsed send
          is mail nobody received, and a bounce rate measured over rehearsals
          reads perfect for a site with no mailbox at all. */}
      <Figure value={sends?.sentDryRun ?? 0} muted />
      <Figure value={sends?.bounced ?? 0} tone={over ? 'bad' : undefined} />
      <Figure value={sends?.replied ?? 0} note={`${formatExact(sends?.interested ?? 0)} keen`} />
      <Figure value={sends?.won ?? 0} />

      <td className="px-3 py-2 text-right align-middle whitespace-nowrap">
        <span
          className={cn(
            'font-mono text-[12px] tabular',
            rate === null && 'text-muted-foreground/50',
            over && 'text-destructive',
          )}
        >
          {formatPercent(rate, 1)}
        </span>
        <span className="block font-mono text-[11px] text-muted-foreground tabular">
          ceiling {formatPercent(OUTREACH_BOUNCE_PAUSE_RATE, 0)}
        </span>
      </td>
    </tr>
  )
}

function Figure({
  value,
  note,
  tone,
  muted,
}: {
  value: number
  note?: string
  tone?: 'bad' | 'warn'
  muted?: boolean
}) {
  return (
    <td className="px-3 py-2 text-right align-middle whitespace-nowrap">
      <span
        className={cn(
          'font-mono text-[12px] tabular',
          (value === 0 || muted) && 'text-muted-foreground/60',
          tone === 'bad' && value > 0 && 'text-destructive',
          tone === 'warn' && value > 0 && 'text-warning',
        )}
      >
        {formatExact(value)}
      </span>
      {note && value > 0 && (
        <span className="block font-mono text-[11px] text-muted-foreground tabular">{note}</span>
      )}
    </td>
  )
}

/**
 * Whether any of this works.
 *
 * Which source earns links, and which wording earns links — the two questions
 * the closed set of sources and the frozen prompt version exist to
 * make answerable at all. All time and every tenant, because one site writing
 * to twenty domains proves nothing.
 *
 * Real sends only, so this table stays empty while a site is rehearsing. A rate
 * over a handful of sends is not a rate and comes back null, which is why the
 * counts and the rates sit beside each other rather than one standing for the
 * other.
 */
function Efficacy({ data }: { data: OutreachEfficacyData }) {
  const contacted = data.sources.reduce((total, row) => total + row.contacted, 0)
  const won = data.sources.reduce((total, row) => total + row.won, 0)

  return (
    <div className="space-y-4">
      <Panel
        title="Sources"
        meta={`${formatExact(contacted)} contacted · ${formatExact(won)} won`}
        flush
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <Th align="left">Source</Th>
                <Th>Prospects</Th>
                <Th>Vetoed</Th>
                <Th>Contacted</Th>
                <Th>Replied</Th>
                <Th>Won</Th>
                <Th>Reply rate</Th>
                <Th>Win rate</Th>
                <Th>Rewritten</Th>
              </tr>
            </thead>
            <tbody>
              {data.sources.map((row) => (
                <SourceRow key={row.source} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Wordings" meta={formatExact(data.versions.length)} flush>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <Th align="left">Prompt</Th>
                <Th>Approved</Th>
                <Th>Rewritten</Th>
                <Th>Contacted</Th>
                <Th>Replied</Th>
                <Th>Won</Th>
                <Th>Reply rate</Th>
                <Th>Win rate</Th>
              </tr>
            </thead>
            <tbody>
              {data.versions.map((row) => (
                <VersionRow key={row.versionId} row={row} />
              ))}
              {data.versions.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center">
                    <span className="font-mono text-[12px] text-muted-foreground">
                      nothing has been sent for real yet
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  )
}

function SourceRow({ row }: { row: OutreachSourceEfficacyRow }) {
  return (
    <tr className="border-b border-border last:border-b-0">
      <td className="py-2 pr-3 pl-3">
        <span className="font-mono text-[12.5px]">{row.source}</span>
        <span className="block text-[11px] text-muted-foreground">{row.label}</span>
      </td>
      <Figure value={row.prospects} />
      {/* Beside the prospects it produced, never inside them: a source that is
          mostly dismissed is a finding about the source. */}
      <Figure value={row.vetoed} muted />
      <Figure value={row.contacted} />
      <Figure value={row.replied} />
      <Figure value={row.won} />
      <Rate value={row.replyRate} />
      <Rate value={row.winRate} />
      <Rate
        value={row.editedRate}
        note={
          row.approved > 0 ? `${formatExact(row.edited)}/${formatExact(row.approved)}` : undefined
        }
      />
    </tr>
  )
}

function VersionRow({ row }: { row: OutreachVersionEfficacyRow }) {
  return (
    <tr className="border-b border-border last:border-b-0">
      <td className="py-2 pr-3 pl-3">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[12.5px]">{row.prompt}</span>
          <span className="font-mono text-[11px] text-muted-foreground tabular">
            v{row.version}
          </span>
          {row.active && <Tag>live</Tag>}
        </span>
      </td>
      <Figure value={row.approved} />
      <Rate
        value={row.editedRate}
        note={
          row.approved > 0 ? `${formatExact(row.edited)}/${formatExact(row.approved)}` : undefined
        }
      />
      <Figure value={row.contacted} />
      <Figure value={row.replied} />
      <Figure value={row.won} />
      <Rate value={row.replyRate} />
      <Rate value={row.winRate} />
    </tr>
  )
}

/** A rate this small a denominator cannot support arrives null, and the cell
 * says nothing rather than quoting a percentage off two sends. */
function Rate({ value, note }: { value: number | null; note?: string }) {
  return (
    <td className="px-3 py-2 text-right align-middle whitespace-nowrap">
      <span
        className={cn(
          'font-mono text-[12px] tabular',
          value === null && 'text-muted-foreground/50',
        )}
      >
        {formatPercent(value, 1)}
      </span>
      {/* The counts stand whether or not the rate does: a source with four
          rewrites out of seven has said something, and the rate it cannot
          support is the only part that goes. */}
      {note && (
        <span className="block font-mono text-[11px] text-muted-foreground tabular">{note}</span>
      )}
    </td>
  )
}

/**
 * Who this deployment may never write to again.
 *
 * Per workspace, because the list is theirs: somebody who asked us to stop did
 * so on behalf of the site that wrote to them. The reasons are the operational
 * half — a list growing by unsubscribes is a feed working as designed, and one
 * growing by bounces is a prospect source that has stopped earning its credits.
 */
function Suppressions({ rows }: { rows: SuppressionRow[] }) {
  const total = rows.reduce((sum, row) => sum + row.total, 0)

  return (
    <Panel title="Suppressed" meta={formatExact(total)} flush>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-border bg-secondary/30">
              <Th align="left">Workspace</Th>
              <Th>Addresses</Th>
              <Th>Domains</Th>
              <Th align="left">Reasons</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.organizationId} className="border-b border-border last:border-b-0">
                <td className="py-2 pr-3 pl-3 text-[13px]">{row.organization}</td>
                <Figure value={row.emails} />
                <Figure value={row.domains} />
                <td className="px-3 py-2">
                  <span className="flex flex-wrap items-center gap-1.5">
                    {row.reasons.map((reason) => (
                      <Tag key={reason.reason}>
                        {reason.reason} {formatExact(reason.count)}
                      </Tag>
                    ))}
                  </span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center">
                  <span className="font-mono text-[12px] text-muted-foreground">nobody</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

/** Age first, then the one word that says whether the age is good news. A feed
 * read an hour ago that errored is worse than one read yesterday that did not. */
function Cell({
  at,
  now,
  note,
  tone,
}: {
  at: string | null
  now: number
  note?: string
  tone?: 'warn' | 'bad'
}) {
  return (
    <td className="px-3 py-2 text-right align-middle whitespace-nowrap">
      <Age at={at} now={now} className="block" />
      {note && (
        <span
          className={cn(
            'block font-mono text-[11px] text-muted-foreground tabular',
            tone === 'warn' && 'text-warning',
            tone === 'bad' && 'text-destructive',
          )}
        >
          {note}
        </span>
      )}
    </td>
  )
}

/**
 * Which checks a hand-run scan covers.
 *
 * Nothing picked is every check, which is what the nightly pass does. The
 * narrowing exists because re-running one detector against one site is how a
 * detector is debugged, and running all five to watch one of them is four
 * scans' worth of work nobody asked for.
 */
function TypePicker({
  types,
  onChange,
}: {
  types: ActionTypeId[]
  onChange: (next: ActionTypeId[]) => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="group inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card pr-2 pl-2.5 font-mono text-[12px] transition-colors hover:border-border-strong data-[state=open]:border-border-strong">
        <span className={cn(types.length === 0 && 'text-muted-foreground')}>
          {types.length === 0 ? 'all checks' : `${types.length} checks`}
        </span>
        <ChevronDownIcon
          className="size-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
          aria-hidden
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[300px] p-1">
        <DropdownMenuItem onSelect={() => onChange([])} className="gap-2">
          <CheckIcon
            className={cn('size-3.5 shrink-0', types.length > 0 && 'opacity-0')}
            aria-hidden
          />
          <span className="text-[13px]">All checks</span>
        </DropdownMenuItem>
        {ACTION_TYPES.map((type) => (
          <DropdownMenuCheckboxItem
            key={type.id}
            checked={types.includes(type.id)}
            // The menu stays open: picking two of five is the normal case, and
            // a menu that closes on every tick makes that five round trips.
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={(checked) =>
              onChange(
                checked ? [...types, type.id] : types.filter((current) => current !== type.id),
              )
            }
          >
            <span className="font-mono text-[12px]">{type.id}</span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
